import { useQuery } from '@tanstack/react-query'
import type { MeResponse } from '@lobby/shared'
import { api, isApiError } from '../api'
import type { ConversationFilter } from '../lib/conversations'

/** Query keys. Dashboard data (Tenant DO) lives under ['d', clientId, ...] so it can be invalidated together. */
export const qk = {
  me: ['me'] as const,
  profile: (id: string) => ['profile', id] as const,
  calibration: (id: string) => ['calibration', id] as const,
  situations: (id: string) => ['situations', id] as const,
  jobs: (id: string) => ['jobs', id] as const,
  rules: (id: string) => ['rules', id] as const,
  modelCalls: (id: string) => ['model-calls', id] as const,
  dashboard: (id: string) => ['d', id] as const,
  summary: (id: string) => ['d', id, 'summary'] as const,
  conversations: (id: string, filter: ConversationFilter) => ['d', id, 'conversations', filter] as const,
  conversation: (id: string, cid: string) => ['d', id, 'conversation', cid] as const,
  whatsappConfig: ['whatsapp-config'] as const,
  metaPages: ['meta-pages'] as const,
  notices: (id: string) => ['d', id, 'notices'] as const,
  usage: (id: string) => ['d', id, 'usage'] as const,
  audit: (id: string, cid: string) => ['d', id, 'audit', cid] as const,
  suggestions: (id: string) => ['d', id, 'suggestions'] as const,
}

/** Session and client. `null` when not signed in (401). */
export function useMe(refetchInterval: number | false = false) {
  return useQuery<MeResponse | null>({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return await api.me()
      } catch (e) {
        if (isApiError(e, 401)) return null
        throw e
      }
    },
    refetchInterval,
    // Waiting states (Stripe confirmation, channel checks) should progress even in a background tab.
    refetchIntervalInBackground: refetchInterval !== false,
  })
}

/** Returns null on 404 so "not generated yet" is a normal state, not an error. */
async function orNull<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p
  } catch (e) {
    if (isApiError(e, 404)) return null
    throw e
  }
}

export const useProfile = (id: string) =>
  useQuery({ queryKey: qk.profile(id), queryFn: () => orNull(api.profile(id)) })

export const useCalibration = (id: string) =>
  useQuery({ queryKey: qk.calibration(id), queryFn: () => orNull(api.calibration(id)) })

export const useSituations = (id: string) =>
  useQuery({ queryKey: qk.situations(id), queryFn: async () => (await orNull(api.situations(id))) ?? [] })

export const useJobs = (id: string) =>
  useQuery({ queryKey: qk.jobs(id), queryFn: () => orNull(api.jobs(id)) })

/** Every confirmed rule with the preference records behind it. */
export const useRules = (id: string) =>
  useQuery({ queryKey: qk.rules(id), queryFn: async () => (await orNull(api.rules(id))) ?? { rules: [], preferences: [] } })

export const useModelCalls = (id: string, enabled = true) =>
  useQuery({ queryKey: qk.modelCalls(id), queryFn: async () => (await orNull(api.modelCalls(id))) ?? [], enabled })

// ---------- Channels (Worker) ----------

/** Embedded Signup settings. Loaded once; the Facebook SDK is initialised from it. */
export const useWhatsAppConfig = (enabled = true) =>
  useQuery({ queryKey: qk.whatsappConfig, queryFn: api.whatsappConfig, enabled, staleTime: Infinity, retry: 1 })

/**
 * Pages offered after the Facebook login (`pick`: the login state from `?meta=pick&pick=<state>`). The login expires
 * on the Worker, so never retry or refetch it.
 */
export const useMetaPages = (enabled: boolean, pick: string | null = null) =>
  useQuery({
    queryKey: pick ? [...qk.metaPages, pick] : qk.metaPages,
    queryFn: async () => (await api.metaPages(pick)).pages,
    enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

// ---------- Dashboard data (Tenant Durable Object) ----------

// 401, 403 and 429 (rate limited) won't fix themselves by retrying at once.
const dashRetry = (count: number, e: unknown) => count < 2 && !isApiError(e, 401) && !isApiError(e, 403) && !isApiError(e, 429)

/** Open owner notices (channel problems, limits, conversations that need the owner); live `notice` frames add to it. */
export const useNotices = (id: string, enabled = true) =>
  useQuery({ queryKey: qk.notices(id), queryFn: () => api.notices(id), enabled, refetchInterval: 5 * 60_000, retry: dashRetry })

export const useSummary = (id: string, enabled = true) =>
  useQuery({ queryKey: qk.summary(id), queryFn: () => api.summary(id), enabled, refetchInterval: 60_000, retry: dashRetry })

export const useConversations = (id: string, filter: ConversationFilter, enabled = true) =>
  useQuery({
    queryKey: qk.conversations(id, filter),
    queryFn: () => api.conversations(id, filter),
    enabled,
    // The live socket keeps this fresh; the interval is a safety net if it drops.
    refetchInterval: 30_000,
    retry: dashRetry,
  })

export const useConversation = (id: string, cid: string | null) =>
  useQuery({
    queryKey: qk.conversation(id, cid ?? ''),
    queryFn: () => api.conversation(id, cid as string),
    enabled: !!cid,
    retry: dashRetry,
  })

export const useUsage = (id: string, enabled = true) =>
  useQuery({ queryKey: qk.usage(id), queryFn: () => api.usage(id), enabled, retry: dashRetry })

export const useAudit = (id: string, cid: string, enabled = true) =>
  useQuery({ queryKey: qk.audit(id, cid), queryFn: () => api.audit(id, cid), enabled, retry: dashRetry })

export const useSuggestions = (id: string, enabled = true) =>
  useQuery({ queryKey: qk.suggestions(id), queryFn: () => api.suggestions(id), enabled, retry: dashRetry })
